<section class="mod-page rp-page" data-module="roles">
  <header class="page-header">
    <div>
      <p class="eyebrow">Administration</p>
      <h1>Roles &amp; permissions</h1>
      <p class="muted lead">
        Set each desk role’s Access / Read / Write matrix. Manage the underlying catalog on
        <a routerLink="/resources">Resources</a>
        ; assign users from
        <a routerLink="/settings">User management</a>
        — this screen does not replace that editor.
      </p>
    </div>
    @if (canManage()) {
      <div class="btn-row">
        <button type="button" class="btn" (click)="reload()" [disabled]="loading() || busy()">
          Refresh
        </button>
        <button type="button" class="btn primary" (click)="openCreate()" [disabled]="loading()">
          + Add New Role
        </button>
      </div>
    }
  </header>

  @if (message()) {
    <p class="toast">{{ message() }}</p>
  }

  @if (loading()) {
    <p class="muted">Loading roles and permissions…</p>
  } @else if (!canManage()) {
    <p class="toast">Only Admin can open Roles &amp; permissions.</p>
  } @else {
    <div class="rp-layout">
      <aside class="rp-master" aria-label="Roles">
        <div class="rp-panel-heading">
          <p class="rp-eyebrow">Roles</p>
          <h3>{{ roles().length }} available</h3>
        </div>
        <nav class="rp-role-list">
          @for (role of roles(); track role.id) {
            <button
              type="button"
              class="rp-role"
              [class.rp-role--active]="sameId(role.id, selectedRoleId())"
              [class.rp-role--inactive]="role.status === 'inactive'"
              [attr.aria-current]="sameId(role.id, selectedRoleId()) ? 'true' : null"
              (click)="selectRole(role.id)"
            >
              <span class="rp-role-copy">
                <strong>{{ displayName(role) }}</strong>
                <small>{{ role.roleName }}</small>
              </span>
              <span class="rp-role-count" title="Assigned users">{{ role.assignedUserCount || 0 }}</span>
            </button>
          } @empty {
            <p class="muted">No roles loaded.</p>
          }
        </nav>
      </aside>

      @if (selectedRole(); as role) {
        <main class="rp-detail">
          <section class="rp-role-summary">
            <div class="rp-summary-main">
              <div class="rp-title-line">
                <h2>{{ displayName(role) }}</h2>
                @if (immutable()) {
                  <span class="rp-fixed-badge">Fixed full access</span>
                }
                @if (!immutable()) {
                  <button type="button" class="btn" (click)="openEdit()">Edit role</button>
                }
              </div>
              <p class="muted">{{ role.description || 'No role description provided.' }}</p>
            </div>
            <dl class="rp-metadata">
              <div>
                <dt>Role code</dt>
                <dd>{{ role.roleName }}</dd>
              </div>
              <div>
                <dt>Module</dt>
                <dd>All modules</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd><span class="pill">{{ role.status || 'active' }}</span></dd>
              </div>
              <div>
                <dt>Permissions</dt>
                <dd>{{ grantedCount() }} / {{ availableCount() }}</dd>
              </div>
            </dl>
            @if (immutable()) {
              <div class="rp-readonly-note">
                Super Admin always has full access. Its permissions are read-only and cannot be changed.
              </div>
            }
          </section>

          <section class="rp-matrix-card">
            <div class="rp-matrix-head">
              <div>
                <h3>Permission matrix</h3>
                <p class="muted">Access · Read · Write — columns that don’t apply are merged away per row</p>
              </div>
              @if (dirty()) {
                <span class="rp-dirty">Unsaved changes</span>
              }
            </div>

            <div class="rp-table-wrap">
              <table class="rp-matrix">
                <colgroup>
                  <col class="rp-col-resource" />
                  <col class="rp-col-action" />
                  <col class="rp-col-action" />
                  <col class="rp-col-action" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Access</th>
                    <th>Read</th>
                    <th>Write</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of matrixRows(); track row.resource.id) {
                    <tr>
                      <td>
                        <strong>{{ row.resource.label || row.resource.resourceName }}</strong>
                        <div class="muted code">{{ row.resource.resourceName }}</div>
                      </td>
                      @for (cell of actionViewsFor(row); track cell.action) {
                        @if (cell.spacer) {
                          <td class="rp-cell rp-cell--spacer" aria-hidden="true"></td>
                        } @else {
                          <td
                            class="rp-cell rp-cell--action"
                            [attr.colspan]="cell.colspan > 1 ? cell.colspan : null"
                            [class.rp-cell--span]="cell.colspan > 1"
                          >
                            <div class="rp-action-slot">
                              <label class="rp-check">
                                <input
                                  type="checkbox"
                                  [checked]="isChecked(cell.permissionCode)"
                                  [disabled]="immutable() || busy()"
                                  (change)="
                                    onToggle(
                                      row,
                                      cell.action,
                                      cell.permissionCode,
                                      $any($event.target).checked
                                    )
                                  "
                                />
                                <span class="sr-only">{{ cell.action }}</span>
                              </label>
                            </div>
                          </td>
                        }
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            @if (!immutable()) {
              <footer class="rp-actions">
                <button type="button" class="btn" [disabled]="!dirty() || busy()" (click)="cancelDraft()">
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn primary"
                  [disabled]="!dirty() || busy()"
                  (click)="requestSave()"
                >
                  Save permissions
                </button>
              </footer>
            }
          </section>
        </main>
      }
    </div>
  }
</section>

@if (createOpen()) {
  <div class="admin-overlay" (click)="closeCreate()">
    <div class="admin-modal" role="dialog" aria-modal="true" aria-labelledby="createRoleTitle" (click)="$event.stopPropagation()">
      <header class="admin-modal-head">
        <h3 id="createRoleTitle">Add New Role</h3>
        <button type="button" class="admin-modal-close" (click)="closeCreate()" aria-label="Close">×</button>
      </header>
      <div class="admin-modal-body">
        <div class="form-grid">
          <label>
            Role name
            <input class="field" [(ngModel)]="newRoleName" name="roleName" placeholder="e.g. tehsildar" autocomplete="off" />
          </label>
          <label>
            Description
            <input class="field" [(ngModel)]="newDescription" name="roleDescription" placeholder="Short description" />
          </label>
        </div>
        <p class="muted hint">
          Use lowercase letters, numbers, underscore. Reserved: <code>admin</code>, <code>citizen</code>.
          Set the permission matrix after create.
        </p>
      </div>
      <footer class="admin-modal-foot">
        <button type="button" class="btn" (click)="closeCreate()" [disabled]="busy()">Cancel</button>
        <button type="button" class="btn primary" [disabled]="busy() || !newRoleName.trim()" (click)="create()">
          {{ busy() ? 'Creating…' : 'Create role' }}
        </button>
      </footer>
    </div>
  </div>
}

@if (editOpen()) {
  <div class="admin-overlay" (click)="closeEdit()">
    <div class="admin-modal" role="dialog" aria-modal="true" aria-labelledby="editRoleTitle" (click)="$event.stopPropagation()">
      <header class="admin-modal-head">
        <h3 id="editRoleTitle">Edit role</h3>
        <button type="button" class="admin-modal-close" (click)="closeEdit()" aria-label="Close">×</button>
      </header>
      <div class="admin-modal-body">
        <div class="form-grid">
          <label>
            Description
            <input class="field" [(ngModel)]="editDescription" name="editDescription" />
          </label>
          <label>
            Status
            <select class="field" [(ngModel)]="editStatus" name="editStatus">
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </label>
        </div>
      </div>
      <footer class="admin-modal-foot">
        <button type="button" class="btn" (click)="closeEdit()" [disabled]="busy()">Cancel</button>
        <button type="button" class="btn primary" [disabled]="busy()" (click)="saveEdit()">
          {{ busy() ? 'Saving…' : 'Save' }}
        </button>
      </footer>
    </div>
  </div>
}

@if (confirmSaveOpen()) {
  <div class="admin-overlay" (click)="closeConfirmSave()">
    <div class="admin-modal" role="dialog" aria-modal="true" aria-labelledby="savePermTitle" (click)="$event.stopPropagation()">
      <header class="admin-modal-head">
        <h3 id="savePermTitle">Save permissions?</h3>
        <button type="button" class="admin-modal-close" (click)="closeConfirmSave()" aria-label="Close">×</button>
      </header>
      <div class="admin-modal-body">
        <p class="muted" style="margin:0">
          Apply the selected permissions to
          <strong>{{ displayName(selectedRole()!) }}</strong>?
          Users must refresh or re-login to pick up sidebar changes from an already-open session.
        </p>
      </div>
      <footer class="admin-modal-foot">
        <button type="button" class="btn" (click)="closeConfirmSave()" [disabled]="busy()">Cancel</button>
        <button type="button" class="btn primary" [disabled]="busy()" (click)="confirmSave()">
          {{ busy() ? 'Saving…' : 'Apply permissions' }}
        </button>
      </footer>
    </div>
  </div>
}
